import { ITINERARY_EXPIRED } from '@/src/constants/itineraryStatus'
import {
    BaggageInfo,
    CurrencyConversionDetails,
    Customer,
    Fare,
    Flight,
    ItineraryResponse,
    MiniRuleItem,
    Passenger,
    SearchCriteria,
    SmbMandatoryFareMapType,
    UpdatedFareResponse,
    User,
    VasAncillary
} from '@/src/types/itinerary'
import { isEmpty } from '@/src/utils/generalUtils/isEmpty'
import { logError } from '@/src/utils/logError'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { transformItineraryData } from '@/src/utils/rtkQuery/transformItineraryData'
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { addonsApi } from './addonsApi'

interface ItineraryQueryResponse {
    itineraryData: ItineraryResponse
    flights: Flight[]
    itineraryStatus: string
    searchCriteria: SearchCriteria
    faresList: Fare[][]
    meta: Record<string, any>
    passengers: Passenger[]
    customer: Customer
    insurance: {
        insuranceList: VasAncillary[]
        selectedInsurances: VasAncillary[]
        isInsuranceTermsAndConditionsChecked: boolean
    }
    fareFamilyFares: Fare[]
    metaData: {
        priceChangePopupData: {
            updatedFareResponse: UpdatedFareResponse
            isPriceChange: boolean
            priceChangePopupShown: boolean
            oldPrice: number
            newPrice: number
            callPrePaymentOnAcceptPriceChange?: boolean
        }
    }
    currencyConversionDetails: CurrencyConversionDetails
    smbMandatoryFareMap: SmbMandatoryFareMapType
}
// Complete flight info response interface
interface FlightInfoResponse {
    freeBaggageInfoResponse: {
        BI: BaggageInfo
    }
    miniRuleResponse: MiniRuleItem[]
}

interface ApplicableCouponTagItem {
    icon: string
    amount?: number
    code: string
    status: boolean
    targetLink: string
    sequence: number
    isRT: boolean
    isOW: boolean
    _id: string
    domain: string[]
    en: {
        heading: string
        description: string
    }
    ar: {
        heading: string
        description: string
    }
}

interface ApplicableCouponsListResponse {
    message: string
    tags: {
        tags: ApplicableCouponTagItem[]
        message?: string
    }
}

export const itineraryApi = createApi({
    reducerPath: 'itineraryApi',
    baseQuery: fetchBaseQuery({
        prepareHeaders: (headers, api) => {
            const defaultHeaders = setCommonHeaders(headers)
            switch (api.endpoint) {
                case 'getFlightInfo':
                    defaultHeaders.delete('Content-Type')
                    defaultHeaders.set('flowType', 'flyin')
                default:
                    break
            }
            return defaultHeaders
        }
    }),

    tagTypes: ['Itinerary'],

    // Keep unused cached data for 10 minutes (600 seconds)
    keepUnusedDataFor: 600,

    endpoints: builder => ({
        // ========== QUERIES (READ DATA) ==========

        // Main itinerary data query
        getItineraryData: builder.query<ItineraryQueryResponse, { itineraryId: string }>({
            query: ({ itineraryId }) => `/itin/itinerary/${itineraryId}`,
            transformResponse: transformItineraryData,
            transformErrorResponse: (response, meta, arg) => {
                const { status, data } = response
                return {
                    status: status,
                    error: ITINERARY_EXPIRED,
                    message: `Request failed with status ${status}`,
                    itineraryId: arg.itineraryId,
                    data: data || {}
                }
            },
            providesTags: (result, error, { itineraryId }) => [{ type: 'Itinerary', id: itineraryId }]
        }),

        getFlightInfo: builder.query<FlightInfoResponse, { itineraryId: string }>({
            query: ({ itineraryId }) => `/itin/api/itinerary/${itineraryId}/info/flightInfo`,
            transformErrorResponse: (response, meta, arg) => {
                const { status, data } = response
                return {
                    status: status,
                    message: `Flight info request failed with status ${status}`,
                    itineraryId: arg.itineraryId,
                    data: data || {}
                }
            },
            providesTags: (result, error, { itineraryId }) => [{ type: 'Itinerary', id: `${itineraryId}-flightInfo` }]
        }),

        getApplicableCouponsList: builder.query<ApplicableCouponTagItem[], { itineraryId: string }>({
            query: ({ itineraryId }) => `/itin/itinerary/${itineraryId}/applicableCouponsList`,
            transformResponse: (response: ApplicableCouponsListResponse | ApplicableCouponTagItem[]) => {
                if (Array.isArray(response)) return response
                return response?.tags?.tags || []
            }
        }),

        getUserByItinerary: builder.query<User, { itineraryId: string }>({
            query: ({ itineraryId }) => `/itin/v2/itinerary/${itineraryId}/user`,
            providesTags: (result, error, { itineraryId }) => [{ type: 'Itinerary', id: `${itineraryId}-user` }]
        }),

        // ========== MUTATIONS (UPDATE DATA) ==========

        // 🎯 SINGLE UPDATE MUTATION - Same endpoint, different payloads based on step
        updateItinerary: builder.mutation({
            query: ({ payload }) => ({
                url: `/itin/itinerary/update`,
                method: 'POST',
                body: payload
            })
            // No invalidatesTags - just make the API call and let component handle next steps
        }),
        // Create itinerary mutation
        createItinerary: builder.mutation<ItineraryResponse, { payload: any }>({
            query: ({ payload }) => ({
                url: '/itin/itinerary/create',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    // Wait for the mutation to complete and get the response
                    const { data: createResponse } = await queryFulfilled
                    if (createResponse?.itineraryId) {
                        const itineraryId = createResponse.itineraryId
                        if (createResponse.flights && createResponse.searchCriteria) {
                            // Transform the create response to match what getItineraryData expects
                            const transformedData = transformItineraryData(createResponse)
                            // Use upsertQueryEntries - this bypasses transformResponse and prevents double transformation
                            dispatch(
                                itineraryApi.util.upsertQueryEntries([
                                    {
                                        endpointName: 'getItineraryData',
                                        arg: { itineraryId },
                                        value: transformedData
                                    }
                                ])
                            )
                        }
                    }
                } catch (error) {
                    logError('createItineraryMutation', error)
                }
            }
        }),

        updateFareSelection: builder.mutation<
            any,
            {
                payload: any
                itineraryId: string
                selectedFare: Fare
            }
        >({
            query: ({ payload }) => ({
                url: '/itin/fare-card/update',
                method: 'POST',
                body: payload
            }),
            async onQueryStarted({ itineraryId, selectedFare }, { dispatch, queryFulfilled }) {
                // Optimistically update the cache
                const patchResult = dispatch(
                    itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                        if (!isEmpty(draft?.fareFamilyFares)) {
                            // const removeVasSelections = selectedFare.fareGroupInformation?.ctBrandName !== 'REGULAR'

                            draft.fareFamilyFares.forEach((fare: Fare) => {
                                fare.selected =
                                    fare.fareGroupInformation?.ctBrandName ===
                                    selectedFare.fareGroupInformation?.ctBrandName
                            })

                            // Mark all fares as not selected if any fare family other than regular is selected
                            // if (removeVasSelections && draft?.faresList?.[0]) {
                            //     draft.faresList[0].forEach((fare: Fare) => {
                            //         fare.selected = false
                            //     })
                            // }
                        }
                    })
                )

                try {
                    // Wait for the mutation to complete
                    await queryFulfilled
                    const newCouponTags = await dispatch(
                        itineraryApi.endpoints.getApplicableCouponsList.initiate(
                            { itineraryId },
                            { forceRefetch: true }
                        )
                    ).unwrap()
                    // Keep itinerary meta.couponList in sync with applicable list (same shape as meta.tags.tags)
                    dispatch(
                        itineraryApi.util.updateQueryData('getItineraryData', { itineraryId }, draft => {
                            if (!draft.meta) draft.meta = {}
                            draft.meta.couponList = { tags: { tags: newCouponTags } }
                        })
                    )
                    dispatch(addonsApi.util.invalidateTags(['Addons']))
                } catch {
                    // If the mutation fails, revert the optimistic update
                    patchResult.undo()
                }
            }
        })
    })
})

// Export hooks for usage in components
export const {
    useGetItineraryDataQuery,
    useLazyGetItineraryDataQuery,
    useGetFlightInfoQuery,
    useLazyGetFlightInfoQuery,
    useGetApplicableCouponsListQuery,
    useLazyGetApplicableCouponsListQuery,
    useGetUserByItineraryQuery,
    useLazyGetUserByItineraryQuery,
    useUpdateItineraryMutation,
    useCreateItineraryMutation,
    useUpdateFareSelectionMutation
} = itineraryApi

// Export the API slice for store configuration
export default itineraryApi
export type { ApplicableCouponTagItem }
